import type { NavNode, NavTopLevel, Params, PathBuilder } from './types';

import { useTranslation } from 'react-i18next';
import { href, useParams } from 'react-router';

import {
  BookImageIcon,
  FlaskConicalIcon,
  GraduationCapIcon,
  GroupIcon,
  MessagesSquareIcon,
  ShoppingCartIcon,
  StoreIcon,
  UserLockIcon,
} from '@valwin/facto/icons';

import { ROUTES } from '~shared/routes';

import { buildCalendarItem } from './buildCalendarItem';
import { buildExtranetGroup } from './buildExtranetGroup';
import { buildNewsItem } from './buildNewsItem';
import { buildValwinGroup } from './buildValwinGroup';
import { useAccessChecker } from './useAccessChecker';

export function useNavigationItems({
  isAdmin,
  isValwinAdmin,
}: Params): NavTopLevel[] {
  const { group = '' } = useParams();
  const { t } = useTranslation();
  const buildPath: PathBuilder = (route) => href(route, { group });
  const checkAccess = useAccessChecker();

  const items: NavTopLevel[] = [];

  if (checkAccess({ isAdmin: isAdmin && isValwinAdmin })) {
    items.push(buildValwinGroup(buildPath, t));
  }

  // Section: Réseau
  const networkChildren: NavNode[] = [];

  if (checkAccess({ isAdmin })) {
    networkChildren.push(
      {
        kind: 'link',
        id: 'pharmacies',
        label: t('menu pharmacies'),
        icon: StoreIcon,
        to: buildPath(ROUTES.pharmacies),
      },
      {
        kind: 'link',
        id: 'pharmacies-affiliations',
        label: t('menu pharmacyGroups'),
        icon: GroupIcon,
        to: buildPath(ROUTES.pharmaciesAffiliations),
      }
    );
  }

  if (networkChildren.length > 0) {
    items.push({
      kind: 'section',
      id: 'section-network',
      label: t('menu network'),
      children: networkChildren,
    });
  }

  // Section: Communication
  const communicationChildren: NavNode[] = [
    buildCalendarItem(buildPath, t, checkAccess, isAdmin),
  ];

  if (checkAccess({ isAdmin })) {
    communicationChildren.push(buildNewsItem(buildPath, t, checkAccess));
  }

  if (checkAccess({ isAdmin: true, requiredFeatures: ['PDF_MAG'] })) {
    communicationChildren.push({
      kind: 'link',
      id: 'mags',
      label: t('menu magazines'),
      icon: BookImageIcon,
      to: buildPath(ROUTES.mags),
    });
  }

  if (checkAccess({ isAdmin: true, requiredFeatures: ['SHOP_PROMOTIONS'] })) {
    communicationChildren.push({
      kind: 'link',
      id: 'promotions',
      label: t('menu promotions'),
      icon: ShoppingCartIcon,
      to: buildPath(ROUTES.promotions.root),
    });
  }

  if (checkAccess({ isAdmin, requiredFeatures: ['CAMPAIGNS'] })) {
    communicationChildren.push({
      kind: 'link',
      id: 'campagnes',
      label: t('menu campaigns'),
      icon: FlaskConicalIcon,
      to: buildPath(ROUTES.campaigns.root),
    });
  }

  if (communicationChildren.length > 0) {
    items.push({
      kind: 'section',
      id: 'section-communication',
      label: t('menu communication'),
      children: communicationChildren,
    });
  }

  // Section: Outils
  const toolsChildren: NavNode[] = [];

  if (checkAccess({ isAdmin, requiredFeatures: ['CAREER'] })) {
    toolsChildren.push({
      kind: 'link',
      id: 'carrieres',
      label: t('menu jobOffers'),
      icon: GraduationCapIcon,
      to: buildPath(ROUTES.career.root),
    });
  }

  if (checkAccess({ isAdmin, requiredFeatures: ['EXTRANET'] })) {
    toolsChildren.push(buildExtranetGroup(buildPath, t, checkAccess));
  }

  if (checkAccess({ isAdmin: true, requiredFeatures: ['FORUM'] })) {
    toolsChildren.push({
      kind: 'link',
      id: 'forum',
      label: t('menu intranet'),
      icon: MessagesSquareIcon,
      to: buildPath(ROUTES.forum),
    });
  }

  if (checkAccess({ isAdmin })) {
    toolsChildren.push({
      kind: 'link',
      id: 'settings',
      label: t('menu configuration'),
      icon: UserLockIcon,
      to: buildPath(ROUTES.settings),
    });
  }

  if (toolsChildren.length > 0) {
    items.push({
      kind: 'section',
      id: 'section-tools',
      label: t('menu tools'),
      children: toolsChildren,
    });
  }

  return items;
}
